import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, ArrowLeft, CheckCircle2, HandHeart } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

const MINISTRIES = [
  "Hospitality Team", "Worship Team", "1st Sunday Choir", "Youth & Children's Ministry",
  "Outreach Team", "Media / Tech Team", "Singles Ministry", "Married Couples Ministry",
  "Bible Book Club", "Finance Team", "Mother's Board", "Deacons Board",
  "Intercessory Team", "Pastor's Aid",
];

export default function Volunteer() {
  const [params] = useSearchParams();
  const preselectedMinistry = params.get("ministry") || MINISTRIES[0];

  const [form, setForm] = useState({
    ministry: preselectedMinistry,
    name: "", email: "", phone: "",
    is_member: false, availability: "", reason: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preselectedMinistry && MINISTRIES.includes(preselectedMinistry)) {
      setForm((f) => ({ ...f, ministry: preselectedMinistry }));
    }
  }, [preselectedMinistry]);

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.phone) delete payload.phone;
      if (!payload.availability) delete payload.availability;
      await api.post("/volunteer-signups", payload);
      setDone(true);
      toast.success("Thank you! A team lead will reach out soon.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main id="main-content" className="bg-[#050505] text-white min-h-[70vh] flex items-center" data-testid="volunteer-success-page">
        <Seo title="Volunteer Signup Received" path="/volunteer" />
        <div className="max-w-2xl mx-auto px-5 lg:px-10 py-24 text-center">
          <div className="w-20 h-20 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-6 ring-1 ring-electric/40">
            <CheckCircle2 className="w-10 h-10 text-electric" />
          </div>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-4">Thank You</div>
          <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter mb-5">
            You said <span className="italic text-electric text-pulse">yes.</span>
          </h1>
          <p className="text-zinc-300 leading-relaxed mb-8">
            We received your signup for <strong className="text-white">{form.ministry}</strong>. A team leader will reach out within the next few days to welcome you and walk you through next steps.
          </p>
          <div className="flex gap-3 justify-center">
            <Link to="/ministries" className="inline-flex items-center gap-2 border border-white/20 text-white px-6 py-3 rounded-full font-bold hover:border-electric hover:text-electric transition" data-testid="volunteer-back">
              <ArrowLeft className="w-4 h-4" /> All Ministries
            </Link>
            <Link to="/" className="inline-flex items-center gap-2 bg-electric text-black px-6 py-3 rounded-full font-bold hover:bg-[#4ade80] transition" data-testid="volunteer-home">
              Home
            </Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-[#050505] text-white" data-testid="volunteer-page">
      <Seo
        title={`Volunteer · ${form.ministry}`}
        description={`Join the ${form.ministry} at The Push Center. Sign up to serve and step into your gifts.`}
        path="/volunteer"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/images/worship-team.jpg" alt="" className="w-full h-full object-cover opacity-70 bg-zoom" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/20" />
          <div className="absolute inset-0 lightning-grid opacity-30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <Link to="/ministries" className="inline-flex items-center gap-2 text-zinc-400 hover:text-electric text-sm font-bold uppercase tracking-wider mb-6" data-testid="vol-back-link">
            <ArrowLeft className="w-4 h-4" /> All Ministries
          </Link>
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            Volunteer Signup
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] max-w-4xl">
            Serve the <span className="italic text-electric">{form.ministry}.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            You weren't designed to sit on the sidelines. Sign up below and a team leader will walk you through the next steps.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 lg:px-10 pb-32">
        <form onSubmit={submit} className="space-y-6 p-8 lg:p-10 rounded-2xl bg-[#0a0a0a] border border-white/5" data-testid="volunteer-form">
          <Field label="Ministry">
            <select className={inputCls} value={form.ministry} onChange={(e) => setForm({ ...form, ministry: e.target.value })} data-testid="vol-ministry">
              {MINISTRIES.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </Field>

          <div className="grid sm:grid-cols-2 gap-5">
            <Field label="Your Name">
              <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="vol-name" />
            </Field>
            <Field label="Email">
              <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="vol-email" />
            </Field>
          </div>

          <Field label="Phone (optional)">
            <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="vol-phone" />
          </Field>

          <label className="flex items-center gap-3 cursor-pointer p-4 rounded-lg bg-[#050505] border border-white/5">
            <input type="checkbox" checked={form.is_member} onChange={(e) => setForm({ ...form, is_member: e.target.checked })} className="w-4 h-4 accent-[#22C55E]" data-testid="vol-member" />
            <span className="text-zinc-200 text-sm">I'm a current member of The Push Center</span>
          </label>

          <Field label="Availability (when can you serve?)">
            <input className={inputCls} value={form.availability} onChange={(e) => setForm({ ...form, availability: e.target.value })} placeholder="e.g., Sundays 9–11 AM, Wednesday evenings" data-testid="vol-availability" />
          </Field>

          <Field label="Why are you interested in this ministry?">
            <textarea rows={5} className={inputCls} required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Share what's stirring on your heart..." data-testid="vol-reason" />
          </Field>

          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center gap-2 bg-electric text-black px-7 py-4 rounded-full font-bold tracking-wide hover:bg-[#4ade80] hover:-translate-y-0.5 transition-all duration-300 disabled:opacity-60"
            data-testid="vol-submit"
          >
            <HandHeart className="w-4 h-4" />
            {submitting ? "Submitting..." : "Sign Me Up"}
          </button>
        </form>
      </section>
    </main>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold mb-2">{label}</label>
      {children}
    </div>
  );
}
